import React, { useEffect, useMemo, useState } from "react";
import { Button } from "antd";
import { BannerSkeleton } from "../../BannerSkeleton";
import {
  isNavigable,
  lightenColor,
} from "../../../../Utils/StayUpToDate/RenderComponent";
import { useDesign } from "../../../../context/DesignContext";
import { useLoadTypeContentData } from "../../../../Utils/customHooks/UpToDateContent/useLoadTypeContent";
import { isHexDark } from "../../../../Utils/HelperFunction/HelperFunction";
import { darkenColor } from "../../../../Screens/DashboardView/StayUpToDate/LandingPageComponents/LinksComponent/Components/hoverUtils";
import "../../StayUpToDate.css";
import "../HighlightDataSection/HighlightDataSection.css";
import { FeedSectionEmptyState } from "../FeedSectionEmptyState";
import { websiteEditorFeedEmptyMessage } from "../../../../Screens/DashboardView/MasterTemplateEditor/websiteEditorFeedEmptyMessage";

interface LatestPodcastDataProps {
  user_id: string;
  username: string;
  component: any;
  brand_color?: string;
  handleRedirect?: (...args: any[]) => void;
  isHandleNavigate?: boolean;
  from?: string;
  designTokens?: any;
  isMobileView?: boolean;
  creatorPageSlug?: string;
  showEmptyInEditor?: boolean;
}

const LatestPodcastData: React.FC<LatestPodcastDataProps> = ({
  user_id,
  username,
  component,
  brand_color,
  handleRedirect,
  isHandleNavigate,
  from,
  designTokens: designTokensProp,
  isMobileView = false,
  showEmptyInEditor = false,
}) => {
  const {
    fontFamily,
    primaryFont,
    secondaryFont,
    primaryColor,
    secondaryColor,
    tertiaryColor,
    forthColor,
    templateName,
  } = useDesign();

  const designTokens = designTokensProp ?? {
    fontFamily,
    primaryFontFamily: primaryFont,
    secondaryFontFamily: secondaryFont,
    primaryColor,
    secondaryColor,
    tertiaryColor,
    forthColor,
    templateName,
  };

  const effectiveTemplateName = (
    designTokens?.templateName || ""
  ).toLowerCase();
  const isDefaultTheme = effectiveTemplateName === "default";

  const buttonBg = isDefaultTheme
    ? "#000000"
    : designTokens?.forthColor || "#000000";

  const buttonOverrideClass = isDefaultTheme ? "highlight-btn-override" : "";

  const [skeletonVisible, setSkeletonVisible] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setSkeletonVisible(false), 400);
    return () => clearTimeout(t);
  }, []);

  const view: "left" | "right" =
    component?.details === "right" ? "right" : "left";
  // Keep right-side artwork in the markup. Narrow stacking is CSS (600px /
  // website LiB container), not templateDevice=mobile (890px).
  const applyPresentation = view === "right";

  const instanceKey = `latest-podcast-front-${component?.id ?? ""}`;
  const { dataPages, isLoading, isFetching } = useLoadTypeContentData(
    username,
    user_id,
    "podcast",
    "PODCAST",
    "",
    "DESC",
    [],
    undefined,
    [],
    "all",
    instanceKey,
    instanceKey,
    true,
  );

  const latestEpisode = useMemo(() => {
    const firstPage = dataPages?.[0];
    if (!firstPage?.data || !Array.isArray(firstPage.data)) return null;
    return firstPage.data[0] ?? null;
  }, [dataPages]);

  const isDataLoading = skeletonVisible || isLoading || isFetching;

  const episodeUrl = useMemo(() => {
    if (!latestEpisode) return "";
    return (
      latestEpisode.permalink ||
      latestEpisode.url ||
      latestEpisode.external_link ||
      ""
    );
  }, [latestEpisode]);

  const imageUrl = useMemo(() => {
    if (!latestEpisode) return "/assets/image-bg.jpeg";
    return (
      latestEpisode.media_url ||
      latestEpisode.thumbnail ||
      latestEpisode.product_image ||
      "/assets/image-bg.jpeg"
    );
  }, [latestEpisode]);

  const displayName =
    (component?.name || "").trim() ||
    latestEpisode?.caption ||
    latestEpisode?.content_title ||
    "Latest Podcast Episode";

  const episodeName =
    latestEpisode?.caption ||
    latestEpisode?.content_title ||
    "Latest Podcast Episode";

  const cta = component?.cta_placeholder || "Listen Now";

  if (isDataLoading) {
    return <BannerSkeleton designTokens={designTokens} />;
  }

  if (!latestEpisode) {
    if (showEmptyInEditor) {
      return (
        <FeedSectionEmptyState
          title={displayName}
          designTokens={designTokens}
          message={websiteEditorFeedEmptyMessage("platform", {
            platform: "Podcast",
          })}
        />
      );
    }
    return null;
  }

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Fire tracking callback the same way HighlightDataSection does
    handleRedirect?.(
      component?.id,
      imageUrl,
      "",
      displayName,
      displayName,
      "",
      from,
    );

    if (episodeUrl) {
      window.open(episodeUrl, "_blank");
    }
  };

  return (
    <>
      {isDefaultTheme && (
        <style>{`
          .uptodate-wrapper .highlight-btn-override,
          .uptodate-wrapper .highlight-btn-override:hover,
          .uptodate-wrapper .highlight-btn-override:focus,
          .uptodate-wrapper .highlight-btn-override:active {
            color: #fff !important;
          }
          .uptodate-wrapper .highlight-btn-override span { color: #fff !important; }
        `}</style>
      )}
      <div className="mainDiv">
        <p
          className="component-name"
          style={{
            textAlign: "start",
            overflowWrap: "anywhere",
            fontFamily: designTokens?.primaryFontFamily,
          }}
        >
          {displayName}
        </p>
      </div>

      <div
        className="outter-section"
        style={{
          backgroundColor: designTokens?.primaryColor || brand_color,
          borderColor: designTokens?.tertiaryColor,
          marginTop: 0,
        }}
      >
        <div
          className="highlight-link-tag"
          style={{ backgroundColor: designTokens?.primaryColor }}
        >
          <div
            className={`highlight-data-section${isMobileView ? " highlight-data-section-mobile highlights-view-mobile" : ""}${applyPresentation ? " highlight-presentation-right" : ""}`}
            style={{
              ...(applyPresentation
                ? {
                    flexDirection: "row-reverse",
                    backgroundColor: designTokens?.primaryColor,
                  }
                : { backgroundColor: designTokens?.primaryColor }),
              border: designTokens?.tertiaryColor
                ? "none"
                : "1px solid #e6e7eb",
            }}
          >
            <div
              className={`highlight-media${applyPresentation ? " highlight-media-right" : ""} ${isMobileView ? "highlight-media-mobile" : ""} podcast`}
            >
              <img
                src={imageUrl}
                alt={displayName}
                className="highlight-image podcast-media"
                onError={(e: any) => {
                  e.currentTarget.src = "/assets/image-bg.jpeg";
                }}
              />
            </div>

            <div
              className={`highlight-details${isMobileView ? " highlight-details-mobile" : ""}`}
            >
              <div
                className={`highlight-name${isMobileView ? " highlight-name-mobile" : ""}`}
                style={{
                  cursor: isNavigable(component) ? "pointer" : "default",
                  ...(isMobileView && { fontSize: "14px" }),
                fontFamily: designTokens?.secondaryFontFamily
                }}
              >
                {episodeName}
              </div>

              {cta && (
                <div className="text-center">
                  <Button
                    className={`highlight-shop-button${
                      designTokens?.secondaryColor
                        ? " custom-button-with-hover"
                        : ""
                    } ${buttonOverrideClass}${
                      isMobileView ? " highlight-shop-button-mobile" : ""
                    }`}
                    style={{
                      backgroundColor: buttonBg,
                      color: isDefaultTheme
                        ? "#fff"
                        : designTokens?.tertiaryColor || "#fff",
                      fontFamily: designTokens?.secondaryFontFamily,
                      ...(designTokens?.secondaryFontFamily
                        ? ({
                            ["--highlight-font" as any]:
                              designTokens?.secondaryFontFamily,
                          } as any)
                        : {}),
                      ...(buttonBg
                        ? ({
                            ["--hover-bg" as any]:isDefaultTheme ? lightenColor("#000000", 20): isHexDark(buttonBg) ? lightenColor(buttonBg, 20) : darkenColor(buttonBg, 10),
                          } as any)
                        : {}),
                    }}
                    onClick={handleClick}
                  >
                    {cta}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LatestPodcastData;
